import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { captureException } from 'src/shared/helpers/captureException';
import { useHistory } from 'react-router-dom';

import { appClient } from 'src/config/apolloV3';
import { layout, mediaQuery } from '@modernhealth/vital-foundation';
import {
    ContentCardsRowSkeleton,
    PageHeader,
    PageHeaderSkeleton,
    Button,
    pxToRem,
} from '@modernhealth/vital-components';
import { useGetCompletedScheduledContentQuery } from '../documents/getCompletedScheduledContent.generated';
import { CompletedDailyPausesList } from '../components/CompletedDailyPausesList';
import { useMediaQuery } from '@react-hook/media-query';
import { ScheduledContentEdge } from 'src/generated/webapp_gql';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

const BackButton = styled(Button)({
    marginTop: pxToRem(4),
    marginLeft: pxToRem(-18),
});

const CompletedDailyPausesContainer = styled.div({
    position: 'relative',
    marginTop: pxToRem(48),
});

const headerHeight = 300;
const rowHeight = 264;

export const CompletedDailyPauses: React.FC<RouteComponentProps> = () => {
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    useEffect(() => {
        function handleResize() {
            setWindowHeight(window.innerHeight);
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const screenSizeMd = useMediaQuery(mediaQuery.minWidth('md', true));
    const screenSizeXl = useMediaQuery(mediaQuery.minWidth('xl', true));

    let rowTilesCount = 1;

    if (screenSizeMd) {
        rowTilesCount = 2;
    }

    if (screenSizeXl) {
        rowTilesCount = 5;
    }

    const containerHeight = windowHeight - headerHeight;
    const rowCount = Math.round(containerHeight / rowHeight);
    const minFetchCount = 10;
    const calculatedFetchCount = rowCount > 0 ? rowCount * rowTilesCount : 1;
    const [fetchCount] = useState(
        calculatedFetchCount < minFetchCount ? minFetchCount : calculatedFetchCount,
    );

    const history = useHistory();
    const [lastElement, setLastElement] = useState();
    const [shouldFetch, setShouldFetch] = useState(false);

    const { data, loading, error, fetchMore } = useGetCompletedScheduledContentQuery({
        client: appClient,
        errorPolicy: 'all',
        variables: { first: fetchCount },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
    });

    const completedDailyPauses = data?.completedScheduledContent;

    const observer = useRef(
        new IntersectionObserver(
            entries => {
                const first = entries[0];

                if (first.isIntersecting) {
                    setShouldFetch(true);
                }

                setShouldFetch(false);
            },
            { threshold: 1 },
        ),
    );

    useEffect(() => {
        if (shouldFetch && completedDailyPauses?.pageInfo.hasNextPage && !loading) {
            fetchMore({
                variables: { cursor: completedDailyPauses?.pageInfo.endCursor },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                    const newEdges = fetchMoreResult?.completedScheduledContent?.edges;
                    const pageInfo = fetchMoreResult?.completedScheduledContent?.pageInfo;

                    return newEdges?.length &&
                        previousResult.completedScheduledContent?.edges?.length
                        ? {
                              completedScheduledContent: {
                                  __typename: previousResult.completedScheduledContent?.__typename,
                                  edges: [
                                      ...previousResult.completedScheduledContent?.edges,
                                      ...newEdges,
                                  ],
                                  pageInfo,
                              },
                          }
                        : previousResult;
                },
            });
        }
    }, [
        completedDailyPauses?.pageInfo.endCursor,
        completedDailyPauses?.pageInfo.hasNextPage,
        fetchMore,
        shouldFetch,
        loading,
    ]);

    useEffect(() => {
        const currentElement = lastElement;
        const currentObserver = observer.current;

        if (currentElement) {
            currentObserver.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                currentObserver.unobserve(currentElement);
            }
        };
    }, [lastElement]);

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    useEffect(() => {
        if (!loading && !error && !data?.completedScheduledContent) {
            captureException(
                new Error(
                    'Missing required data in the response to render completed Daily Pauses page',
                ),
                { response: JSON.stringify(data) },
            );
        }
    }, [data, loading, error]);

    const { t } = useTranslation();

    if (loading && !completedDailyPauses) {
        return (
            <Wrapper>
                <PageHeaderSkeleton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <BackButton
                children={t('dashboard:button_back')}
                title={t('dashboard:button_back')}
                variant="flat"
                size="medium"
                icon="arrow-left"
                onClick={() => {
                    history.push('/pause/stats');
                }}
            />
            <PageHeader>{t('dailyPause:streaks.completed.title')}</PageHeader>
            <CompletedDailyPausesContainer>
                {completedDailyPauses && (
                    <CompletedDailyPausesList
                        links={completedDailyPauses.edges as ScheduledContentEdge[]}
                        setLastElement={setLastElement}
                    />
                )}
                {loading && (
                    <div css={{ position: 'relative' }}>
                        <CenteredSpinner />
                    </div>
                )}
            </CompletedDailyPausesContainer>
        </Wrapper>
    );
};
